import { useAtom } from 'jotai';
import _ from 'lodash';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import useFormatter from '../../common/useFormatter';
import useGetNamedColor from '../../common/useGetNamedColor';
import DropdownSelector from '../../components/actions/DropdownSelector';
import * as Icon from '../../components/icons';
import { Container } from '../../components/layout/ContainerFlex';
import { ActionBar } from '../../components/nav/ActionBar';
import { StatGroup } from '../../components/stats/StatGroup';
import KpiGenericCard from '../../dashboard/kpi/helpers/KpiGenericCard';
import { KPI_TAG_METADATA } from '../../dashboard/kpi/kpiModel';
import ActualityAggregatedStatsMetricPieChart from '../charts/ActualityAggregatedStatsMetricPieChart';
import useActualityAggregationFieldDescriptors from '../hooks/useActualityAggregationFieldDescriptors';
import useActualityAggregationMetricDescriptors from '../hooks/useActualityAggregationMetricDescriptors';
import useGetActualityAggregationGroupColors from '../hooks/useGetActualityAggregationGroupColors';
import {
  actualityAggregatedStatsByGroupAndTime,
  actualityAggregatedStatsGroupBy,
  actualityAggregatedStatsGroupBySecondary,
  actualityAggregatedStatsSelectedAggregatedMetric,
  actualityAggregatedStatsSelectedGroups,
  actualityAggregatedStatsTotal,
  actualityAggregatedStatsViewMode,
} from '../store/actuality.kpi.state';
import { ActualityAggregatedHqExtendedStatsDataColumn } from '../store/actuality.kpi.types';
import { actualityAggregatedStatsColorBySelected } from '../store/actuality.kpi.ui.state';
import { getActualityAggregationMetricDescriptorsMap } from '../store/feedMetrics';

/**
 * Example usage patterns for the refactored FeedProcessPie component
 */
const FeedDashboardPanel: React.FC = () => {
  const { t } = useTranslation('feed');

  // Get current aggregation settings
  const groupBy = useRecoilValue(actualityAggregatedStatsGroupBy);
  const groupBySecondary = useRecoilValue(
    actualityAggregatedStatsGroupBySecondary,
  );

  // Use the proper group and time data that includes both primary and secondary groupings
  const data = useRecoilValue(actualityAggregatedStatsByGroupAndTime);
  const selectedMetric = useRecoilValue(
    actualityAggregatedStatsSelectedAggregatedMetric,
  );
  const selectedGroups = useRecoilValue(actualityAggregatedStatsSelectedGroups);

  const formatter = useFormatter();
  const getNamedColor = useGetNamedColor();
  const metricDescriptorsMap = getActualityAggregationMetricDescriptorsMap(
    t,
    formatter,
  );
  const aggFieldDescriptors = useActualityAggregationFieldDescriptors();
  const warehouseTotalStats = useRecoilValue(actualityAggregatedStatsTotal);
  const getGroupColors = useGetActualityAggregationGroupColors();

  // Get all available metric descriptors (filtered and validated)
  const allMetricDescriptors = useActualityAggregationMetricDescriptors();

  // Color selection state
  const [colorBySelected, setColorBySelected] = useAtom(
    actualityAggregatedStatsColorBySelected,
  );

  // View mode state
  const viewModeKpi = useRecoilValue(actualityAggregatedStatsViewMode);

  // Get all available metrics from the descriptors (these are already filtered and validated)
  const metricsToShow: ActualityAggregatedHqExtendedStatsDataColumn[] = _.keys(
    allMetricDescriptors,
  ) as ActualityAggregatedHqExtendedStatsDataColumn[];

  // Filter data based on selected groups (if any are selected)
  const filteredData = React.useMemo(() => {
    if (_.isEmpty(selectedGroups)) {
      return data; // Show all data when no groups are selected
    }

    // Filter data to only include rows that match the selected groups
    return data.filter(dataRow => {
      return _.some(selectedGroups, selectedGroup => {
        return selectedGroup[groupBy] === dataRow[groupBy];
      });
    });
  }, [data, selectedGroups, groupBy]);

  // Group metrics by category
  const metricsByCategory = React.useMemo(() => {
    const grouped: Record<
      string,
      ActualityAggregatedHqExtendedStatsDataColumn[]
    > = {};

    metricsToShow.forEach(metric => {
      const descriptor = allMetricDescriptors[metric];
      if (descriptor?.category) {
        // Check if metric has meaningful data in the filtered data
        const totalValue = (warehouseTotalStats[metric] as number) || 0;
        const hasChartData = filteredData.some(d => {
          const value = (d[metric] as number) || 0;
          return value > 0;
        });

        // Only include metrics that have either total value or chart data
        if (totalValue > 0 || hasChartData) {
          if (!grouped[descriptor.category]) {
            grouped[descriptor.category] = [];
          }
          grouped[descriptor.category].push(metric);
        }
      }
    });

    return grouped;
  }, [metricsToShow, allMetricDescriptors, warehouseTotalStats, filteredData]);

  // Helper function to determine grid columns based on metric count
  const getGridCols = (
    metricCount: number,
    hasManySeriesCards: boolean = false,
  ): string => {
    // If any card has many series, use full width layout
    if (hasManySeriesCards) return 'grid-cols-1';

    if (metricCount <= 2) return 'grid-cols-1';
    if (metricCount <= 4) return 'grid-cols-2';
    if (metricCount <= 6) return 'grid-cols-3';
    if (metricCount <= 8) return 'grid-cols-3';
    return 'grid-cols-3';
  };

  // Helper function to determine if a card has many series (for bar charts)
  const hasManySeries = (chartData: any[]): boolean => {
    // Consider "many series" if there are more than 12 data points for bar charts
    return chartData && chartData.length > 24;
  };

  // Helper function to get individual card width class based on series count
  const getCardWidthClass = (chartData: any[]): string => {
    if (!chartData || chartData.length === 0) return '';

    // Cards with many series get full width
    if (chartData.length > 12) return 'col-span-full';

    // Default: single column width
    return '';
  };

  // Color selection options with dynamic titles based on current grouping
  const colorByOptions = _.keyBy(
    [
      {
        key: 'group',
        title: `Color by ${aggFieldDescriptors[groupBy]?.title || groupBy}`,
        icon: <Icon.Settings4 className="h-4 w-4 fill-current" />,
      },
      {
        key: 'bucket',
        title: `Color by ${aggFieldDescriptors[groupBySecondary]?.title || groupBySecondary}`,
        icon: <Icon.Settings4 className="h-4 w-4 fill-current" />,
      },
    ],
    'key',
  );

  // Debug: log the current state for verification
  console.log('FeedDashboardPanel state:', {
    viewModeKpi,
    groupBy,
    groupBySecondary,
    selectedMetric,
    selectedGroupsCount: selectedGroups.length,
    dataLength: data.length,
    filteredDataLength: filteredData.length,
    metricCategories: Object.keys(metricsByCategory),
  });

  return (
    <Container col hasOverflowY>
      {/* Dashboard Controls */}
      <ActionBar className="p-2">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-2">
            <span className="text-menu-active text-sm font-medium">
              {viewModeKpi === 'summary'
                ? t('Summary Mode: Grouped by {{groupBy}}', {
                    groupBy: aggFieldDescriptors[groupBy]?.title || groupBy,
                  })
                : t('Metrics grouped by {{groupBy}} and {{groupBySecondary}}', {
                    groupBy: aggFieldDescriptors[groupBy]?.title || groupBy,
                    groupBySecondary:
                      aggFieldDescriptors[groupBySecondary]?.title ||
                      groupBySecondary,
                  })}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <DropdownSelector
              panelMode
              className="text-xxs"
              label={t('Color by')}
              value={colorBySelected}
              values={_.keys(colorByOptions)}
              renderValue={key => colorByOptions[key]?.title ?? key}
              onChange={async key => {
                setColorBySelected(key as 'group' | 'bucket');
              }}
              buttonTransparent
              w_sm
            />
          </div>
        </div>
      </ActionBar>
      {Object.entries(metricsByCategory).map(
        ([category, categoryMetrics]): React.ReactElement => {
          const categoryMetadata =
            KPI_TAG_METADATA[category as keyof typeof KPI_TAG_METADATA];

          // First, generate chart data for all metrics to determine if any have many series
          const metricsWithChartData = categoryMetrics.map(metric => {
            const metricDescriptor = allMetricDescriptors[metric];
            const metricValue = (warehouseTotalStats[metric] as number) || 0;
            const metricTitle = metricDescriptor?.title;

            // Create chart data for this metric - behavior depends on view mode
            const chartData = (() => {
              // Determine slicing strategy based on view mode
              if (viewModeKpi === 'summary') {
                // Summary mode: Group by primary grouping dimension
                const slicingGroup = groupBy;

                // Aggregate values by the primary grouping dimension
                const groupedData = _.groupBy(filteredData, slicingGroup);

                return _.map(groupedData, (groupItems, groupKey) => {
                  // Sum up values for this group
                  const totalValue = _.sumBy(
                    groupItems,
                    item => (item[metric] as number) || 0,
                  );

                  if (totalValue === 0) return null; // Skip empty groups

                  // Use the first item of the group for other properties
                  const representativeItem = groupItems[0];

                  // Format the group label using field descriptors
                  const getGroupLabel = (groupValue: string) => {
                    const fieldDescriptor = aggFieldDescriptors[slicingGroup];
                    if (
                      fieldDescriptor?.formatter &&
                      representativeItem[slicingGroup] !== undefined
                    ) {
                      return fieldDescriptor.formatter(
                        representativeItem[slicingGroup],
                      );
                    }
                    return groupValue;
                  };

                  // Get colors for this group
                  const [primaryColor, secondaryColor] = (() => {
                    try {
                      return getGroupColors(
                        representativeItem,
                        [groupBy, groupBySecondary],
                        ['bucket', 'group'],
                      );
                    } catch {
                      return (
                        metricDescriptor?.color ||
                        getNamedColor(`${metric}-${groupKey}`)
                      );
                    }
                  })();

                  // Format the value
                  const formattedValue = metricDescriptor?.getFormatted
                    ? metricDescriptor.getFormatted(totalValue)
                    : {
                        value: totalValue.toString(),
                        fullString: totalValue.toString(),
                        unit: '',
                        raw: totalValue,
                        unitCode: '',
                      };

                  return {
                    id: `${metric}-${groupKey}`,
                    title: getGroupLabel(groupKey),
                    value: totalValue,
                    label: getGroupLabel(groupKey),
                    color: [primaryColor, secondaryColor],
                    formatted: formattedValue,
                    originalData: representativeItem,
                    groupKey,
                    groupSize: groupItems.length,
                  };
                }).filter(Boolean); // Remove null entries
              } else {
                // Details mode: Multi-select and stack by secondary grouping
                const hasTimeGrouping =
                  groupBySecondary === 'eventHour' ||
                  groupBySecondary === 'eventDay' ||
                  groupBySecondary === 'eventWeek';
                const hasNonTimeGrouping =
                  groupBy !== 'eventHour' &&
                  groupBy !== 'eventDay' &&
                  groupBy !== 'eventWeek';

                if (hasNonTimeGrouping) {
                  // Group filtered data by the secondary grouping dimension (for chart slicing)
                  const slicingGroup = groupBySecondary;

                  // Aggregate values by the secondary grouping dimension
                  const groupedData = _.groupBy(filteredData, slicingGroup);

                  return _.map(groupedData, (groupItems, groupKey) => {
                    // Sum up values for this group
                    const totalValue = _.sumBy(
                      groupItems,
                      item => (item[metric] as number) || 0,
                    );

                    if (totalValue === 0) return null; // Skip empty groups

                    // Use the first item of the group for other properties
                    const representativeItem = groupItems[0];

                    // Format the group label using field descriptors
                    const getGroupLabel = (groupValue: string) => {
                      const fieldDescriptor = aggFieldDescriptors[slicingGroup];
                      if (
                        fieldDescriptor?.formatter &&
                        representativeItem[slicingGroup] !== undefined
                      ) {
                        return fieldDescriptor.formatter(
                          representativeItem[slicingGroup],
                        );
                      }
                      return groupValue;
                    };

                    // Get colors for this group
                    const [primaryColor, secondaryColor] = (() => {
                      try {
                        return getGroupColors(
                          representativeItem,
                          [groupBy, groupBySecondary],
                          ['bucket', 'group'],
                        );
                      } catch {
                        return (
                          metricDescriptor?.color ||
                          getNamedColor(`${metric}-${groupKey}`)
                        );
                      }
                    })();

                    // Format the value
                    const formattedValue = metricDescriptor?.getFormatted
                      ? metricDescriptor.getFormatted(totalValue)
                      : {
                          value: totalValue.toString(),
                          fullString: totalValue.toString(),
                          unit: '',
                          raw: totalValue,
                          unitCode: '',
                        };

                    return {
                      id: `${metric}-${groupKey}`,
                      title: getGroupLabel(groupKey),
                      value: totalValue,
                      label: getGroupLabel(groupKey),
                      color: [primaryColor, secondaryColor],
                      formatted: formattedValue,
                      originalData: representativeItem,
                      groupKey,
                      groupSize: groupItems.length,
                    };
                  }).filter(Boolean); // Remove null entries
                } else {
                  // Time-based visualization (existing logic for time-only grouping)
                  return filteredData
                    .map((d, i) => {
                      const value = (d[metric] as number) || 0;

                      if (value === 0) return null; // Skip empty time periods

                      // Get colors
                      const [primaryColor, secondaryColor] = (() => {
                        try {
                          return getGroupColors(
                            d,
                            [groupBy, groupBySecondary],
                            ['bucket', 'group'],
                          );
                        } catch {
                          return (
                            metricDescriptor?.color ||
                            getNamedColor(`${metric}-${i}`)
                          );
                        }
                      })();

                      // Format value
                      const formattedValue = metricDescriptor?.getFormatted
                        ? metricDescriptor.getFormatted(value)
                        : {
                            value: value.toString(),
                            fullString: value.toString(),
                            unit: '',
                            raw: value,
                            unitCode: 'NUMBER',
                          };

                      // Create time-based labels
                      const getTimeLabel = (dataItem: any, index: number) => {
                        // Try to format using the secondary grouping field (usually time)
                        if (
                          dataItem[groupBySecondary] !== undefined &&
                          dataItem[groupBySecondary] !== null
                        ) {
                          const fieldDescriptor =
                            aggFieldDescriptors[groupBySecondary];
                          if (fieldDescriptor?.formatter) {
                            const formatted = fieldDescriptor.formatter(
                              dataItem[groupBySecondary],
                            );
                            if (formatted) return formatted;
                          }
                          // Fallback to raw value display
                          return `${fieldDescriptor?.title || groupBySecondary}: ${dataItem[groupBySecondary]}`;
                        }

                        // Fallback to primary grouping if secondary is not available
                        if (
                          dataItem[groupBy] !== undefined &&
                          dataItem[groupBy] !== null
                        ) {
                          const fieldDescriptor = aggFieldDescriptors[groupBy];
                          if (fieldDescriptor?.formatter) {
                            const formatted = fieldDescriptor.formatter(
                              dataItem[groupBy],
                            );
                            if (formatted) return formatted;
                          }
                          return `${fieldDescriptor?.title || groupBy}: ${dataItem[groupBy]}`;
                        }

                        return `Period ${index + 1}`;
                      };

                      const timeLabel = getTimeLabel(d, i);

                      return {
                        id: `${metric}-${i}`,
                        title: timeLabel,
                        value,
                        label: timeLabel,
                        color: [primaryColor, secondaryColor],
                        formatted: formattedValue,
                        originalData: d,
                      };
                    })
                    .filter(Boolean); // Remove null entries
                }
              }
            })();

            return {
              metric,
              metricDescriptor,
              metricValue,
              chartData,
            };
          });

          // Get grid layout based on number of metrics (not series count)
          const gridCols = getGridCols(categoryMetrics.length, false);

          return (
            <StatGroup
              key={category}
              title={
                <div className="text-menu-text">
                  <div>{categoryMetadata?.title || category}</div>
                  <p className="text-sm opacity-50">
                    {categoryMetadata?.description || ''}
                  </p>
                </div>
              }
              classNameStatGroupItems={`grid items-stretch w-full p-fix flex-col !gap-1 justify-stretch content-stretch !space-x-none !space-y-0 ${gridCols}`}
            >
              {metricsWithChartData.map(
                ({ metric, metricDescriptor, metricValue, chartData }) => {
                  const cardWidthClass = getCardWidthClass(chartData);

                  return (
                    <div key={metric} className={cardWidthClass}>
                      <KpiGenericCard
                        title={metricDescriptor?.legend}
                        subtitle={metricDescriptor?.description}
                        category={metricDescriptor?.category}
                        value={
                          metricDescriptor?.getFormatted
                            ? metricDescriptor.getFormatted(metricValue).value
                            : null
                        }
                        kpi={metricDescriptor}
                        unit={
                          metricDescriptor?.getFormatted
                            ? metricDescriptor.getFormatted(metricValue).unit
                            : null
                        }
                        // status={status}
                        // trend={metricDescriptor?.trend}
                        chartData={chartData}
                        defaultChartType={
                          metricDescriptor?.recommendedVisualization
                            ?.chartType?.[0] || 'bar'
                        }
                        allowedChartTypes={
                          metricDescriptor?.recommendedVisualization
                            ?.chartType || ['bar', 'pie']
                        }
                        // showChartTypeSelector={true}
                        containerHeight={250}
                        enableExport={true}
                        // showActions={true}
                        // enableFeatureToggles={true}
                        // showRelated={true}
                        // showTrend={true}
                      />
                    </div>
                  );
                },
              )}
            </StatGroup>
          );
        },
      )}

      {/* <StatGroup>
        <KpiGenericCardExamples />
      </StatGroup> */}

      {/* Time-Based Stacked Bar Chart */}
      {/* <FeedTimeBasedStackedBarChart
        title={t`Time-Based Performance Analysis`}
        className="mb-6"
      /> */}

      <StatGroup
        title={t`Warehouse Stats`}
        classNameStatGroupItems={`grid items-stretch w-full p-fix flex-col !gap-1 justify-stretch content-stretch !space-x-none !space-y-0 grid-cols-2`}
      >
        {/* Original pie chart component */}
        <ActualityAggregatedStatsMetricPieChart
          title={t`Direct vs Indirect Time`}
          row={warehouseTotalStats}
          metrics={['idleTimeMs', 'totalEmployeeActivityDuration']}
          metricGroups={[
            {
              title: t`Idle Time`,
              metrics: ['idleTimeMs'],
            },
            {
              title: t`Direct Time`,
              metrics: ['totalEmployeeActivityDuration'],
            },
          ]}
        />

        {/* Example 1: Using the new generic component with pie chart and center stats */}
        {/* <GenericAggregatedStatsChart
          title={t`Direct vs Indirect Time (Generic Pie)`}
          row={warehouseTotalStats}
          metrics={['idleTimeMs', 'totalEmployeeActivityDuration']}
          metricGroups={[
            {
              title: t`Idle Time`,
              metrics: ['idleTimeMs'],
            },
            {
              title: t`Direct Time`,
              metrics: ['totalEmployeeActivityDuration'],
            },
          ]}
          chartType="pie"
          chartTypeConfig={{ showCenterPie: true, innerRadius: 0.65 }}
        /> */}

        {/* Example 2: Using stacked-bar variant */}
        {/* <GenericAggregatedStatsChart
          title={t`Direct vs Indirect Time (Stacked Bar)`}
          row={warehouseTotalStats}
          metrics={['idleTimeMs', 'totalEmployeeActivityDuration']}
          metricGroups={[
            {
              title: t`Idle Time`,
              metrics: ['idleTimeMs'],
            },
            {
              title: t`Direct Time`,
              metrics: ['totalEmployeeActivityDuration'],
            },
          ]}
          chartType="stacked-bar"
        /> */}

        {/* Example 3: Using groupped-bar variant with custom configuration */}
        {/* <GenericAggregatedStatsChart
          title={t`Direct vs Indirect Time (Grouped Bar Chart)`}
          row={warehouseTotalStats}
          metrics={['idleTimeMs', 'totalEmployeeActivityDuration']}
          chartType="groupped-bar"
          chartTypeConfig={{
            containerHeight: 200,
            showAxisLabels: true,
          }}
        /> */}

        {/* <GenericMetricChart
          // title={t`Direct vs Indirect Time`}
          // row={warehouseTotalStats}
          metrics={['idleTimeMs', 'totalEmployeeActivityDuration']}
          metricGroups={[
            {
              title: t`Idle Time`,
              metrics: ['idleTimeMs'],
            },
          ]}
        /> */}

        {/* Example: Generic KPI Card with Bar Chart */}
        {/* <KpiGenericCard
          title={t`Performance Overview`}
          subtitle={t`Net Lines Performance Trend`}
          category="Performance"
          value={warehouseTotalStats.netLinesPerformance}
          unit="%"
          status="good"
          trend="up"
          chartData={data.map((d, i) => {
            // Use the proper color system based on colorBy atom
            const [primaryColor, secondaryColor] = getGroupColors(d, [
              'eventHour',
            ]);

            return {
              id: `hour-${i}`,
              value: d.netLinesPerformance || 0,
              label: `Hour ${i + 1}`,
              color: [primaryColor, secondaryColor],
              formatted: {
                value: `${(d.netLinesPerformance || 0).toFixed(1)}%`,
                fullString: `${(d.netLinesPerformance || 0).toFixed(1)}%`,
                unit: '%',
                raw: d.netLinesPerformance || 0,
                unitCode: 'PERCENT',
              },
            };
          })}
          defaultChartType="bar"
          allowedChartTypes={['bar', 'pie', 'line']}
          showChartTypeSelector={true}
          containerHeight={200}
          enableExport={true}
          showActions={true}
        /> */}
        {/* Example: Generic KPI Card with Pie Chart */}
        {/* <KpiGenericCard
          title={t`Activity Distribution`}
          subtitle={t`Idle vs Active Time`}
          category="Time Management"
          value={
            warehouseTotalStats.idleTimeMs +
            warehouseTotalStats.totalEmployeeActivityDuration
          }
          unit="ms"
          status="warning"
          chartData={[
            {
              id: 'idle',
              value: warehouseTotalStats.idleTimeMs || 0,
              label: t`Idle Time`,
              color: getGroupColors(warehouseTotalStats, ['eventHour']),
              formatted: {
                value: `${Math.round(warehouseTotalStats.idleTimeMs || 0).toLocaleString()}`,
                fullString: `${Math.round(warehouseTotalStats.idleTimeMs || 0).toLocaleString()} ms`,
                unit: 'ms',
                raw: warehouseTotalStats.idleTimeMs || 0,
                unitCode: 'MS',
              },
            },
            {
              id: 'active',
              value: warehouseTotalStats.totalEmployeeActivityDuration || 0,
              label: t`Active Time`,
              color: getGroupColors(warehouseTotalStats, ['eventHour']),
              formatted: {
                value: `${Math.round(warehouseTotalStats.totalEmployeeActivityDuration || 0).toLocaleString()}`,
                fullString: `${Math.round(warehouseTotalStats.totalEmployeeActivityDuration || 0).toLocaleString()} ms`,
                unit: 'ms',
                raw: warehouseTotalStats.totalEmployeeActivityDuration || 0,
                unitCode: 'MS',
              },
            },
          ]}
          forcedChartType="pie"
          showChartTypeSelector={false}
          showCenterPie={true}
          centerPieStat={
            <div className="text-center">
              <div className="text-xs uppercase opacity-70">{t`Total Time`}</div>
              <div className="text-lg font-bold">
                {Math.round(
                  (warehouseTotalStats.idleTimeMs || 0) +
                    (warehouseTotalStats.totalEmployeeActivityDuration || 0),
                )}
              </div>
              <div className="text-xs opacity-70">ms</div>
            </div>
          }
          containerHeight={250}
          enableExport={true}
        /> */}
        {/* Example 1: Default usage - Process Type aggregation */}
        {/* <FeedProcessPie /> */}
        {/* Example 2: Aggregate by Agent Type */}
        {/* <FeedProcessPie aggregateBy="agentType" title={t`Agent Type Summary`} /> */}
        {/* <FeedProcessPie aggregateBy="jobGroupId" title={t`Job Group Summary`} /> */}
        {/* Example 3: Aggregate by Warehouse Area */}
        {/* <FeedProcessPie
          aggregateBy="warehouseArea"
          title={t`Warehouse Area Distribution`}
        /> */}
        {/* Example 4: Aggregate by SKU Group */}
        {/* <FeedProcessPie aggregateBy="skuGroup" title={t`SKU Group Analysis`} /> */}
        {/* Example 5: Dynamic aggregation with selector control */}
        {/* <FeedAggregatedPieChart
          defaultAggregateBy="eventType"
          showAggregationControl={true}
          title={t`Dynamic Activity Analysis`}
        /> */}
        {/* Example 6: Fixed aggregation without control */}
        {/* <FeedAggregatedPieChart
          defaultAggregateBy="consignee"
          showAggregationControl={false}
          title={t`Consignee Distribution`}
        /> */}
      </StatGroup>
    </Container>
  );
};

export default FeedDashboardPanel;
